import * as React from "react";

export const useStreamingText = (text: string, isStreaming: boolean, charsPerFrame = 3): string => {
  const textRef = React.useRef(text);
  textRef.current = text;

  const revealedRef = React.useRef(0);
  const prevRevealedTextRef = React.useRef("");
  const rafRef = React.useRef<number | undefined>(undefined);
  const wasStreamingRef = React.useRef(false);
  const [displayText, setDisplayText] = React.useState(() => (isStreaming ? "" : text));

  React.useEffect(() => {
    if (!isStreaming) {
      wasStreamingRef.current = false;
      return;
    }

    if (!wasStreamingRef.current) {
      revealedRef.current = 0;
      prevRevealedTextRef.current = "";
      setDisplayText("");
    }
    wasStreamingRef.current = true;

    let running = true;
    const animate = () => {
      if (!running) return;
      const currentText = textRef.current;
      const prev = prevRevealedTextRef.current;

      if (prev.length > 0 && !currentText.startsWith(prev)) {
        revealedRef.current = currentText.length;
        prevRevealedTextRef.current = currentText;
        setDisplayText(currentText);
      } else if (revealedRef.current < currentText.length) {
        revealedRef.current = Math.min(revealedRef.current + charsPerFrame, currentText.length);
        const revealed = currentText.slice(0, revealedRef.current);
        prevRevealedTextRef.current = revealed;
        setDisplayText(revealed);
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      running = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [isStreaming, charsPerFrame]);

  if (!isStreaming) return text;
  return displayText;
};
